import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { ValidationNotReadyError } from '../../api/extraction';
import {
  validatingExtractionIdsAtom,
  validationNotReadyExtractionIdsAtom,
} from '../../atoms/spreadsheet-atoms';

// A validation refetch that resolves in one round trip (inline validation on the
// API) must not flash the validating state, so the state only appears once a
// query has been fetching for at least this long. It clears immediately.
export const VALIDATING_STATE_DEBOUNCE_MS = 300;

export const VALIDATING_TOOLTIP = 'Validating...';
export const VALIDATION_NOT_READY_TOOLTIP = 'Validation did not finish in time. Click to retry.';

export type TValidationFetchState = 'idle' | 'validating' | 'not-ready';

interface ValidationQueryLike {
  isFetching: boolean;
  isError: boolean;
  isSuccess?: boolean;
  error?: unknown;
}

export interface IValidationFetchState {
  fetchingExtractionIds: string[];
  notReadyExtractionIds: string[];
}

/**
 * Map the per-extraction validation queries (one per entry of `extractionIds`,
 * in order) back to the extractions that are currently fetching and the ones
 * whose poll budget was exhausted.
 */
export const deriveValidationFetchState = (
  queries: ValidationQueryLike[],
  extractionIds: string[]
): IValidationFetchState => {
  const fetchingExtractionIds: string[] = [];
  const notReadyExtractionIds: string[] = [];
  extractionIds.forEach((extractionId, index) => {
    const query = queries[index];
    if (!query) return;
    if (query.isFetching) {
      fetchingExtractionIds.push(extractionId);
    } else if (query.isError && query.error instanceof ValidationNotReadyError) {
      notReadyExtractionIds.push(extractionId);
    }
  });
  return { fetchingExtractionIds, notReadyExtractionIds };
};

/**
 * Extractions whose validation query has an answer and is not fetching: their
 * result can be published without waiting for the other extractions to settle.
 */
export const deriveSettledExtractionIds = (
  queries: ValidationQueryLike[],
  extractionIds: string[]
): string[] =>
  extractionIds.filter((_, index) => {
    const query = queries[index];
    return !!query && !query.isFetching && (!!query.isSuccess || query.isError);
  });

/**
 * Union of two fetch states (e.g. the review page's extracted queries and the
 * dialog's transformed queries). An extraction that is validating in either is
 * validating; not-ready only applies while nothing is fetching for it.
 */
export const mergeValidationFetchStates = (
  a: { validatingExtractionIds: string[]; notReadyExtractionIds: string[] },
  b: { validatingExtractionIds: string[]; notReadyExtractionIds: string[] }
): { validatingExtractionIds: string[]; notReadyExtractionIds: string[] } => {
  const validatingExtractionIds = Array.from(
    new Set([...a.validatingExtractionIds, ...b.validatingExtractionIds])
  );
  const validating = new Set(validatingExtractionIds);
  const notReadyExtractionIds = Array.from(
    new Set([...a.notReadyExtractionIds, ...b.notReadyExtractionIds])
  ).filter((id) => !validating.has(id));
  return { validatingExtractionIds, notReadyExtractionIds };
};

const sameIds = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id, i) => id === b[i]);

/**
 * Debounced validating state plus the not-ready set for a list of validation
 * queries. `validatingExtractionIds` contains an extraction once its query has
 * been fetching for `debounceMs`; it drops out the moment the fetch settles.
 */
export const useValidationFetchState = (
  queries: ValidationQueryLike[],
  extractionIds: string[],
  debounceMs: number = VALIDATING_STATE_DEBOUNCE_MS
): { validatingExtractionIds: string[]; notReadyExtractionIds: string[] } => {
  const { fetchingExtractionIds, notReadyExtractionIds } = deriveValidationFetchState(
    queries,
    extractionIds
  );
  const fetchingKey = fetchingExtractionIds.join('|');
  const notReadyKey = notReadyExtractionIds.join('|');

  const fetchStartedAt = useRef<Map<string, number>>(new Map());
  const [validatingExtractionIds, setValidatingExtractionIds] = useState<string[]>([]);
  const [notReady, setNotReady] = useState<string[]>([]);

  useEffect(() => {
    setNotReady((prev) => (sameIds(prev, notReadyExtractionIds) ? prev : notReadyExtractionIds));
  }, [notReadyKey]);

  useEffect(() => {
    const now = Date.now();
    const fetching = new Set(fetchingExtractionIds);
    fetchingExtractionIds.forEach((id) => {
      if (!fetchStartedAt.current.has(id)) fetchStartedAt.current.set(id, now);
    });
    Array.from(fetchStartedAt.current.keys()).forEach((id) => {
      if (!fetching.has(id)) fetchStartedAt.current.delete(id);
    });

    // Publish the extractions that have been fetching for the debounce window, then wake
    // up again when the next one crosses it.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const apply = () => {
      const current = Date.now();
      const elapsed = (id: string) => current - (fetchStartedAt.current.get(id) ?? current);
      const next = fetchingExtractionIds.filter((id) => elapsed(id) >= debounceMs);
      setValidatingExtractionIds((prev) => (sameIds(prev, next) ? prev : next));

      const remaining = fetchingExtractionIds
        .map((id) => debounceMs - elapsed(id))
        .filter((ms) => ms > 0);
      if (remaining.length > 0) {
        timer = setTimeout(apply, Math.min(...remaining));
      }
    };
    apply();

    return () => {
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [fetchingKey, debounceMs]);

  return { validatingExtractionIds, notReadyExtractionIds: notReady };
};

/**
 * Refetch the validation queries of one extraction (or of every extraction when
 * no id is given). Used by the retry affordance after a poll budget ran out;
 * React Query does not retry ValidationNotReadyError on its own.
 */
export const useRetryValidation = (): ((extractionId?: string) => void) => {
  const queryClient = useQueryClient();
  return useCallback(
    (extractionId?: string) => {
      queryClient.invalidateQueries({
        queryKey: extractionId
          ? ['extraction-validation', extractionId]
          : ['extraction-validation'],
      });
    },
    [queryClient]
  );
};

/**
 * Validation fetch state of one extraction as published by the review page.
 */
export const useExtractionValidationState = (
  extractionId?: string
): { fetchState: TValidationFetchState; retry: () => void } => {
  const validatingIds = useAtomValue(validatingExtractionIdsAtom);
  const notReadyIds = useAtomValue(validationNotReadyExtractionIdsAtom);
  const retryValidation = useRetryValidation();
  const retry = useCallback(() => retryValidation(extractionId), [retryValidation, extractionId]);

  let fetchState: TValidationFetchState = 'idle';
  if (extractionId && validatingIds.includes(extractionId)) {
    fetchState = 'validating';
  } else if (extractionId && notReadyIds.includes(extractionId)) {
    fetchState = 'not-ready';
  }
  return { fetchState, retry };
};
